import { useContext, useEffect } from 'react';
import { Box, Typography, Stack, Button, Chip } from '@mui/joy';
import { Lock, ArrowRight, CheckCircle, Clock } from '@phosphor-icons/react';

import { LayoutContext } from '../../context';

// Hard paywall: показывается поверх всего контента, когда у юзера state === 'expired'.
// Закрыть нельзя — можно только нажать "Продолжить подготовку" (открывает UpgradeModal)
// или "Выйти".
//
// Правило из фидбека: никогда не оставлять empty state. Здесь единственный
// экран — и он ведёт прямо в оплату.
export default function PaywallScreen() {
    const { openUpgradeModal, logout, user } = useContext(LayoutContext);

    // Автоматически открываем UpgradeModal при попадании на экран — чтобы юзер
    // сразу видел варианты оплаты, а не только причину блокировки.
    useEffect(() => {
        const timer = setTimeout(() => {
            openUpgradeModal('Триал закончился — открой полный доступ, чтобы продолжить подготовку.');
        }, 300);
        return () => clearTimeout(timer);
    }, [openUpgradeModal]);

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 1100,
                bgcolor: 'background.body',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2, sm: 4 },
                overflow: 'auto',
            }}
        >
            <Box
                sx={{
                    maxWidth: 640,
                    width: '100%',
                    bgcolor: 'background.surface',
                    borderRadius: 20,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'lg',
                    p: { xs: 3, sm: 5 },
                    textAlign: 'center',
                }}
            >
                {/* Lock icon */}
                <Box sx={{
                    width: 72, height: 72,
                    borderRadius: 20,
                    bgcolor: 'rgba(184, 76, 49, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                }}>
                    <Lock size={36} weight="bold" color="#B84C31" />
                </Box>

                <Chip
                    size="sm"
                    variant="soft"
                    color="danger"
                    startDecorator={<Clock size={12} weight="fill" />}
                    sx={{ mb: 1.5, fontWeight: 600 }}
                >
                    Триал завершён
                </Chip>

                <Typography level="h2" sx={{ fontWeight: 800, mb: 1, lineHeight: 1.2 }}>
                    5 дней пролетели быстро{user?.first_name ? `, ${user.first_name}` : ''}
                </Typography>

                <Typography level="body-md" sx={{ color: 'text.secondary', mb: 3, maxWidth: 520, mx: 'auto' }}>
                    Ты посмотрел, как устроена платформа. Чтобы продолжить подготовку
                    к USMLE Step 1 — полный доступ ко всему курсу.
                </Typography>

                {/* Benefits summary */}
                <Stack
                    spacing={1}
                    sx={{
                        bgcolor: 'background.body',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 14,
                        p: 2,
                        mb: 3,
                        textAlign: 'left',
                    }}
                >
                    {[
                        '6 000+ мини-тестов + 480+ клинических задач',
                        'Полный план: Биохимия, Иммунология, Кардио, Нефро и др.',
                        '1 200+ текстовых модулей из First Aid',
                        '100+ видео-лекций с PDF-конспектами',
                        'Не сдал Step 1 — возврат 100%',
                    ].map((line) => (
                        <Stack key={line} direction="row" spacing={1} alignItems="flex-start">
                            <CheckCircle size={16} weight="fill" color="#0F7B6C" style={{ flexShrink: 0, marginTop: 3 }} />
                            <Typography level="body-sm">{line}</Typography>
                        </Stack>
                    ))}
                </Stack>

                {/* Primary CTA */}
                <Button
                    size="lg"
                    onClick={() => openUpgradeModal('Оформи полный доступ, чтобы продолжить подготовку.')}
                    endDecorator={<ArrowRight size={18} weight="bold" />}
                    sx={{
                        width: '100%',
                        bgcolor: 'primary.solidBg',
                        color: 'primary.solidColor',
                        fontWeight: 700,
                        fontSize: 16,
                        py: 1.5,
                        mb: 1,
                        '&:hover': { bgcolor: 'primary.solidHoverBg' },
                    }}
                >
                    Продолжить подготовку
                </Button>

                {/* Secondary: logout */}
                <Button
                    variant="plain"
                    color="neutral"
                    size="sm"
                    onClick={logout}
                    sx={{ color: 'text.tertiary' }}
                >
                    Выйти
                </Button>
            </Box>
        </Box>
    );
}
